import type { AnswerOption, ProjectType, QuestionDefinition } from "@reno-estimator/core";

/**
 * Phase 13A: shared question-building helpers for the ten Interior Room
 * Estimators (living room, family room, dining room, bedroom, primary
 * bedroom, home office, laundry room, mudroom, entryway/foyer, hallway).
 *
 * Mirrors packages/core/src/calculation/rules/interior-room-shared.ts:
 * the *mechanism* for asking a flooring/painting/trim/lighting/built-ins/
 * windows-doors question is shared here, but each project type's own
 * questions/<type>.ts file decides which of these it actually asks, in
 * what order, with what copy, and what its own unique question(s) are -
 * see each file for exactly which subset applies. Nothing here is wired
 * into the registry directly; these are building blocks only.
 */

export const FLOORING_TIER_OPTIONS: AnswerOption[] = [
  { id: "not_changing", label: "Not changing" },
  { id: "basic", label: "Basic replacement" },
  { id: "mid_range", label: "Mid-range replacement" },
  { id: "premium", label: "Premium material" }
];

export const PAINTING_OPTIONS: AnswerOption[] = [
  { id: "not_included", label: "Not included" },
  { id: "walls_only", label: "Walls only" },
  { id: "full_room", label: "Full room (walls, ceiling, trim)" }
];

export const TRIM_OPTIONS: AnswerOption[] = [
  { id: "keep_existing", label: "Keep existing trim" },
  { id: "standard_upgrade", label: "Standard trim upgrade" },
  { id: "premium_millwork", label: "Premium / custom millwork" }
];

export const LIGHTING_OPTIONS: AnswerOption[] = [
  { id: "keep_existing", label: "Keep existing lighting" },
  { id: "standard_swap", label: "Standard fixture swap" },
  { id: "statement_lighting", label: "Statement lighting" }
];

export const WINDOWS_DOORS_OPTIONS: AnswerOption[] = [
  { id: "none", label: "No change" },
  { id: "replace_existing", label: "Replace existing" },
  { id: "add_new", label: "Add new" }
];

export function builtInsOptions(customLabel: string): AnswerOption[] {
  return [
    { id: "none", label: "None" },
    { id: "standard", label: "Standard" },
    { id: "custom", label: customLabel }
  ];
}

interface TierQuestionArgs {
  id: string;
  projectType: ProjectType;
  prompt: string;
  helpText?: string;
  options: AnswerOption[];
  order: number;
}

function tierQuestion(args: TierQuestionArgs): QuestionDefinition {
  return {
    id: args.id,
    projectTypes: [args.projectType],
    inputType: "single_select",
    prompt: args.prompt,
    helpText: args.helpText,
    required: false,
    options: args.options,
    order: args.order
  };
}

export function flooringQuestion(projectType: ProjectType, order: number, roomLabel: string): QuestionDefinition {
  return tierQuestion({ id: `${projectType}_flooring_type`, projectType, prompt: `What's happening with flooring in the ${roomLabel}?`, options: FLOORING_TIER_OPTIONS, order });
}

export function paintingQuestion(projectType: ProjectType, order: number): QuestionDefinition {
  return tierQuestion({ id: `${projectType}_painting`, projectType, prompt: "Does this include painting?", options: PAINTING_OPTIONS, order });
}

export function trimQuestion(projectType: ProjectType, order: number): QuestionDefinition {
  return tierQuestion({ id: `${projectType}_trim`, projectType, prompt: "What about trim or millwork?", options: TRIM_OPTIONS, order });
}

export function lightingQuestion(projectType: ProjectType, order: number, helpText?: string): QuestionDefinition {
  return tierQuestion({ id: `${projectType}_lighting`, projectType, prompt: "What's happening with lighting?", helpText, options: LIGHTING_OPTIONS, order });
}

export function builtInsQuestion(projectType: ProjectType, order: number, prompt: string, customLabel: string): QuestionDefinition {
  return tierQuestion({ id: `${projectType}_built_ins`, projectType, prompt, options: builtInsOptions(customLabel), order });
}

export function windowsDoorsQuestion(projectType: ProjectType, order: number, prompt: string): QuestionDefinition {
  return tierQuestion({ id: `${projectType}_windows_doors`, projectType, prompt, options: WINDOWS_DOORS_OPTIONS, order });
}

export function booleanQuestion(id: string, projectType: ProjectType, prompt: string, order: number, helpText?: string): QuestionDefinition {
  return {
    id,
    projectTypes: [projectType],
    inputType: "boolean",
    prompt,
    helpText,
    required: false,
    order
  };
}
